import React, { useEffect, useState, useRef } from 'react';
import Layout from '../components/layout/Layout';
import AchievementCard from '../components/gamification/AchievementCard';
import BadgeCard from '../components/gamification/BadgeCard';
import { Achievement, Badge } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useContext } from 'react';
import { UserContext } from '../store/useStore';
import { achievements, badges } from '../data/achievements';
import AchievementCardSkeleton from '../components/gamification/AchievementCardSkeleton';

const AchievementsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { userAchievements, checkAchievements, userProgress } = useContext(UserContext);
  const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingAchievements, setIsCheckingAchievements] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const initializationRef = useRef(false);

  // Reset initialization when user changes
  useEffect(() => {
    if (currentUser) {
      initializationRef.current = false;
      setHasInitialized(false);
    }
  }, [currentUser?.uid]);

  // Effect to update local state when userAchievements changes
  useEffect(() => {
    console.log('AchievementsPage: userAchievements changed:', userAchievements);
    if (userAchievements && userAchievements.achievements) {
      console.log('AchievementsPage: Updating local state with user achievements:', userAchievements);
      
      const achievementsWithStatus = achievements.map(achievement => {
        const unlocked = userAchievements.achievements.find(a => a.id === achievement.id);
        return {
          ...achievement,
          unlockedAt: unlocked?.unlockedAt
        };
      });

      const badgesWithStatus = badges.map(badge => {
        const unlocked = userAchievements.badges.find(b => b.id === badge.id);
        return {
          ...badge,
          unlockedAt: unlocked?.unlockedAt
        };
      });

      setAllAchievements(achievementsWithStatus);
      setAllBadges(badgesWithStatus);
      setIsLoading(false);
    }
  }, [userAchievements]);

  // Single useEffect to handle initialization and achievement checking
  useEffect(() => {
    if (!currentUser || initializationRef.current) return;

    const initializeAchievements = async () => {
      initializationRef.current = true;
      
      // If we have userAchievements data and it's not empty, use it
      if (userAchievements && userAchievements.achievements && userAchievements.achievements.length > 0) {
        console.log('AchievementsPage: Using existing user achievements:', userAchievements);
        
        const achievementsWithStatus = achievements.map(achievement => {
          const unlocked = userAchievements.achievements.find(a => a.id === achievement.id);
          return {
            ...achievement,
            unlockedAt: unlocked?.unlockedAt
          };
        });

        const badgesWithStatus = badges.map(badge => {
          const unlocked = userAchievements.badges.find(b => b.id === badge.id);
          return {
            ...badge,
            unlockedAt: unlocked?.unlockedAt
          };
        });

        setAllAchievements(achievementsWithStatus);
        setAllBadges(badgesWithStatus);
        setIsLoading(false);
        setHasInitialized(true);
      } else if (!isCheckingAchievements && !hasInitialized) {
        // If no userAchievements data, check for achievements (only once)
        console.log('AchievementsPage: No user achievements found, checking for new ones...');
        setIsCheckingAchievements(true);
        setIsLoading(true);
        
        try {
          await checkAchievements();
          setHasInitialized(true);
        } catch (error) {
          console.error('Error checking achievements:', error);
        } finally {
          setIsCheckingAchievements(false);
          setIsLoading(false);
        }
      }
    };

    initializeAchievements();
  }, [currentUser, checkAchievements]);

  const unlockedAchievements = allAchievements.filter(a => a.unlockedAt);
  const lockedAchievements = allAchievements.filter(a => !a.unlockedAt);
  const unlockedBadges = allBadges.filter(b => b.unlockedAt);
  const lockedBadges = allBadges.filter(b => !b.unlockedAt);

  console.log('AchievementsPage: Render state:', {
    isLoading,
    isCheckingAchievements,
    hasInitialized,
    allAchievementsCount: allAchievements.length,
    unlockedAchievementsCount: unlockedAchievements.length,
    lockedAchievementsCount: lockedAchievements.length,
    userAchievements: userAchievements
  });

  return (
    <Layout>
      <div className="container mx-auto px-4">
        {isLoading && !userAchievements?.achievements?.length ? (
          <div className="space-y-8">
            <div className="mb-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
              <h1 className="text-3xl font-bold mb-2">Achievements</h1>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="h-8 bg-white/20 rounded animate-pulse"></div>
                  <div className="text-sm opacity-90 mt-2">Loading...</div>
                </div>
                <div className="text-center">
                  <div className="h-8 bg-white/20 rounded animate-pulse"></div>
                  <div className="text-sm opacity-90 mt-2">Loading...</div>
                </div>
                <div className="text-center">
                  <div className="h-8 bg-white/20 rounded animate-pulse"></div>
                  <div className="text-sm opacity-90 mt-2">Loading...</div>
                </div>
                <div className="text-center">
                  <div className="h-8 bg-white/20 rounded animate-pulse"></div>
                  <div className="text-sm opacity-90 mt-2">Loading...</div>
                </div>
              </div>
            </div>
            
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {isCheckingAchievements ? 'Checking for New Achievements...' : 'Loading Achievements...'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, index) => (
                  <AchievementCardSkeleton key={index} />
                ))}
              </div>
              {isCheckingAchievements && (
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg">
                    <div className="animate-spin h-4 w-4 border-2 border-yellow-600 rounded-full border-t-transparent mr-2"></div>
                    Checking for new achievements...
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Stats Section */}
          <div className="mb-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
            <h1 className="text-3xl font-bold mb-2">Achievements</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{unlockedAchievements.length}</div>
                <div className="text-sm opacity-90">Achievements</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{unlockedBadges.length}</div>
                <div className="text-sm opacity-90">Badges</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{userAchievements.totalPoints}</div>
                <div className="text-sm opacity-90">Total Points</div>
              </div>
            </div>
          </div>

        {/* Unlocked Achievements */}
        {unlockedAchievements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unlocked Achievements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {unlockedAchievements.map((achievement) => (
                <AchievementCard 
                  key={achievement.id} 
                  achievement={achievement} 
                  isNew={achievement.unlockedAt && 
                    new Date(achievement.unlockedAt).getTime() > Date.now() - 24 * 60 * 60 * 1000} // New if unlocked in last 24 hours
                />
              ))}
            </div>
          </div>
        )}

        {/* Locked Achievements */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Locked Achievements ({lockedAchievements.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lockedAchievements.map((achievement) => (
              <div key={achievement.id} className="bg-gray-100 rounded-lg shadow-md p-4 border border-gray-200 opacity-60">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-gray-500 text-sm">🔒</span>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-700">{achievement.name}</h3>
                    <p className="text-sm text-gray-600">{achievement.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {achievement.condition.description}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        achievement.rarity === 'common' ? 'bg-gray-200 text-gray-700' :
                        achievement.rarity === 'rare' ? 'bg-blue-200 text-blue-700' :
                        achievement.rarity === 'epic' ? 'bg-purple-200 text-purple-700' :
                        'bg-yellow-200 text-yellow-700'
                      }`}>
                        {achievement.rarity.toUpperCase()}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {achievement.points} pts
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Unlocked Badges */}
        {unlockedBadges.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unlocked Badges</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {unlockedBadges.map((badge) => (
                <BadgeCard 
                  key={badge.id} 
                  badge={badge} 
                  isNew={badge.unlockedAt && 
                    new Date(badge.unlockedAt).getTime() > Date.now() - 24 * 60 * 60 * 1000} // New if unlocked in last 24 hours
                />
              ))}
            </div>
          </div>
        )}

        {/* Locked Badges */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Locked Badges ({lockedBadges.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lockedBadges.map((badge) => (
              <div key={badge.id} className="bg-gray-100 rounded-lg shadow-md p-4 border border-gray-200 opacity-60">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-gray-500 text-sm">🔒</span>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-700">{badge.name}</h3>
                    <p className="text-sm text-gray-600">{badge.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {badge.condition.description}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        badge.rarity === 'common' ? 'bg-gray-200 text-gray-700' :
                        badge.rarity === 'rare' ? 'bg-blue-200 text-blue-700' :
                        badge.rarity === 'epic' ? 'bg-purple-200 text-purple-700' :
                        'bg-yellow-200 text-yellow-700'
                      }`}>
                        {badge.rarity.toUpperCase()}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {badge.points} pts
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Display */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Progress</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{userProgress.wordsLearned || 0}</div>
              <div className="text-sm text-gray-600">Words Learned</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{userProgress.streak || 0}</div>
              <div className="text-sm text-gray-600">Current Streak</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600">{userAchievements.totalPoints}</div>
              <div className="text-sm text-gray-600">Total Points</div>
            </div>
          </div>
        </div>

        {/* Manual Check Button */}
        {/* <div className="mt-6 bg-yellow-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Achievement Check</h3>
          <p className="text-sm text-gray-600 mb-4">
            Click the button below to manually check for new achievements based on your current progress.
          </p>
          <button
            onClick={async () => {
              if (!isCheckingAchievements) {
                setIsCheckingAchievements(true);
                try {
                  await checkAchievements();
                } catch (error) {
                  console.error('Error checking achievements:', error);
                } finally {
                  setIsCheckingAchievements(false);
                }
              }
            }}
            disabled={isCheckingAchievements}
            className={`px-4 py-2 rounded-md transition duration-200 ${
              isCheckingAchievements 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-yellow-500 hover:bg-yellow-600 text-white'
            }`}
          >
            {isCheckingAchievements ? 'Checking...' : 'Check for New Achievements'}
          </button>
        </div> */}
        </>
        )}
      </div>
    </Layout>
  );
};

export default AchievementsPage;
